'use client';

// module imports
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// file imports
import { resendVerificationEmail } from '@/redux/slices/authSlice';
import type { AppDispatch, RootState } from '@/redux/store';

interface EmailVerificationPromptProps {
  email: string;
  onResendSuccess?: () => void;
  onResendError?: (error: string) => void;
  onClose?: () => void;
}

export default function EmailVerificationPrompt({ email, onResendSuccess, onResendError, onClose }: EmailVerificationPromptProps) {
  const dispatch = useDispatch<AppDispatch>();
  const { loading: resendLoading, error: resendError } = useSelector((state: RootState) => state.auth);
  const [message, setMessage] = useState('');
  const [cooldown, setCooldown] = useState(0);
  const [countdownInterval, setCountdownInterval] = useState<NodeJS.Timeout | null>(null);

  // Prevent background scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  // Cleanup countdown interval on unmount
  useEffect(() => {
    return () => {
      if (countdownInterval) {
        clearInterval(countdownInterval);
      }
    };
  }, [countdownInterval]);

  // Handle Redux error state
  useEffect(() => {
    if (resendError) {
      setMessage(resendError);
      onResendError?.(resendError);
    }
  }, [resendError, onResendError]);

  const handleResend = async () => {
    if (cooldown > 0) return;

    setMessage('');

    try {
      await dispatch(resendVerificationEmail(email)).unwrap();
      setMessage('Verification email sent successfully!');
      setCooldown(60); // 60 seconds cooldown
      console.log('Starting countdown from 60');
      onResendSuccess?.();

      // Clear any existing interval
      if (countdownInterval) {
        clearInterval(countdownInterval);
      }

      // Start countdown
      const interval = setInterval(() => {
        setCooldown((prev) => {
          console.log('Countdown:', prev - 1);
          if (prev <= 1) {
            clearInterval(interval);
            setCountdownInterval(null);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      setCountdownInterval(interval);
    } catch {
      const errorMessage = resendError || 'Failed to send verification email';
      setMessage(errorMessage);
      onResendError?.(errorMessage);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Overlay with backdrop blur */}
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />

      {/* Modal Content */}
      <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto border border-gray-200 z-[60]" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="flex-shrink-0">
              <svg className="h-6 w-6 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900">Email Verification Required</h3>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          <div>
            <p className="text-sm text-gray-600">
              Your email address <strong>{email}</strong> needs to be verified before you can log in.
            </p>
            <p className="text-sm text-gray-600 mt-2">Please check your inbox for the verification link, or click the button below to resend it.</p>
          </div>

          {message && (
            <div className={`p-3 rounded-md ${message.includes('successfully') ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
              <p className="text-sm">{message}</p>
            </div>
          )}

          <div className="flex space-x-3">
            <button type="button" onClick={handleResend} disabled={resendLoading || cooldown > 0} className="flex-1 inline-flex items-center justify-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed hover:cursor-pointer">
              {resendLoading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Sending...
                </>
              ) : cooldown > 0 ? (
                `Resend in ${cooldown}s`
              ) : (
                'Resend Verification Email'
              )}
            </button>

            <button type="button" onClick={onClose} className="flex-1 inline-flex items-center justify-center px-3 py-1.5 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 hover:cursor-pointer">
              Close
            </button>
          </div>

          <div className="pt-2">
            <p className="text-xs text-gray-500 text-center">You can also try logging in with Google to automatically verify your email.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
